import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Call as CallIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const INDIA_TIME_ZONE = 'Asia/Kolkata';
const API_DATE_TIME_WITHOUT_ZONE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

const clientDetailInitialLoadRequests = new Map();

const getClientDetailInitialLoad = (api, clientId) => {
  const requestKey = String(clientId);
  const existingRequest = clientDetailInitialLoadRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = Promise.all([
    api.get(`/clients/${clientId}`),
    api.get(`/clients/${clientId}/activities`),
    api.get('/masterdata'),
    api.get('/clients/assignable-users'),
  ]).finally(() => {
    if (clientDetailInitialLoadRequests.get(requestKey) === request) {
      clientDetailInitialLoadRequests.delete(requestKey);
    }
  });

  clientDetailInitialLoadRequests.set(requestKey, request);
  return request;
};

const parseApiDateTime = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const trimmedValue = String(value).trim();
  const normalizedValue = API_DATE_TIME_WITHOUT_ZONE_PATTERN.test(trimmedValue)
    ? `${trimmedValue}Z`
    : trimmedValue;
  const date = new Date(normalizedValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = parseApiDateTime(value);
  if (!date) {
    return '-';
  }

  return date.toLocaleString('en-IN', {
    timeZone: INDIA_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
};

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [client, setClient] = useState(null);
  const [clientForm, setClientForm] = useState(null);
  const [activities, setActivities] = useState([]);
  const [leadStages, setLeadStages] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [handoverTimes, setHandoverTimes] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);

  const [commentText, setCommentText] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [briefingCallHappened, setBriefingCallHappened] = useState(false);
  const [selectedLeadStageId, setSelectedLeadStageId] = useState('');
  const [confirmedLeadStageId, setConfirmedLeadStageId] = useState('');
  const [pendingLeadStageId, setPendingLeadStageId] = useState('');
  const [stageConfirmOpen, setStageConfirmOpen] = useState(false);
  const [stageSaving, setStageSaving] = useState(false);
  const [confirmedLeadStatusId, setConfirmedLeadStatusId] = useState('');
  const [pendingLeadStatusId, setPendingLeadStatusId] = useState('');
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusComment, setStatusComment] = useState('');
  const [statusReactivateDate, setStatusReactivateDate] = useState('');
  const [updatingClient, setUpdatingClient] = useState(false);
  const [confirmedAssignedToUserId, setConfirmedAssignedToUserId] = useState('');
  const [pendingAssignedToUserId, setPendingAssignedToUserId] = useState('');
  const [assignmentConfirmOpen, setAssignmentConfirmOpen] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);

  const isAdminUser = String(user?.roleName || '').trim().toLowerCase() === 'admin';

  const getApiErrorMessage = (err, fallback) => {
    const details = err?.response?.data?.errors;
    if (details && typeof details === 'object') {
      const firstKey = Object.keys(details)[0];
      const firstValue = Array.isArray(details[firstKey]) ? details[firstKey][0] : details[firstKey];
      if (firstValue) {
        return String(firstValue);
      }
    }
    return err?.response?.data?.message || fallback;
  };

  const toOptionalId = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const toDateInputValue = (value) => {
    const date = parseApiDateTime(value);
    if (!date) {
      return '';
    }

    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: INDIA_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const dateParts = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
  };

  const toApiDateTimeFromDateInput = (value) => {
    return value ? `${value}T00:00:00` : null;
  };

  const normalizeClient = (raw) => ({
    clientId: raw?.clientId,
    name: raw?.clientName || '',
    phoneNumber: raw?.phoneNumber || '',
    email: raw?.email || '',
    alternatePhoneNumber: raw?.alternatePhoneNumber || '',
    locationId: raw?.locationId ? String(raw.locationId) : '',
    location: raw?.locationName || '',
    subLocationId: raw?.subLocationId ? String(raw.subLocationId) : '',
    subLocation: raw?.subLocationName || '',
    leadSourceId: raw?.leadSourceId ? String(raw.leadSourceId) : '',
    leadSource: raw?.sourceName || '',
    date: raw?.createdAt || null,
    scopeId: raw?.scopeId ? String(raw.scopeId) : '',
    scope: raw?.scopeName || '',
    subScopeId: raw?.subScopeId ? String(raw.subScopeId) : '',
    subScope: raw?.subScopeName || '',
    handoverTimeId: raw?.handoverTimeId ? String(raw.handoverTimeId) : '',
    handover: raw?.handoverTimeframe || '',
    budget: raw?.budget || '',
    leadStatusId: raw?.leadStatusId ? String(raw.leadStatusId) : '',
    leadStatus: raw?.status || '',
    leadStageId: raw?.leadStageId ? String(raw.leadStageId) : '',
    leadStage: raw?.stageName || '',
    assignedToUserId: raw?.assignedToUserId ? String(raw.assignedToUserId) : '',
    assignedToName: raw?.assignedToName || '',
    notes: raw?.notes || '',
    holdUntilDate: raw?.holdUntilDate || null,
  });

  const createClientForm = (normalizedClient) => ({
    name: normalizedClient?.name || '',
    phoneNumber: normalizedClient?.phoneNumber || '',
    email: normalizedClient?.email || '',
    alternatePhoneNumber: normalizedClient?.alternatePhoneNumber || '',
    locationId: normalizedClient?.locationId || '',
    subLocation: normalizedClient?.subLocation || '',
    leadSourceId: normalizedClient?.leadSourceId || '',
    scopeId: normalizedClient?.scopeId || '',
    subScope: normalizedClient?.subScope || '',
    handoverTimeId: normalizedClient?.handoverTimeId || '',
    budget: normalizedClient?.budget || '',
    date: toDateInputValue(normalizedClient?.date),
    assignedToUserId: normalizedClient?.assignedToUserId || '',
    leadStatusId: normalizedClient?.leadStatusId || '',
    holdUntilDate: toDateInputValue(normalizedClient?.holdUntilDate),
    notes: normalizedClient?.notes || '',
  });

  const applyClient = (rawClient) => {
    const normalized = normalizeClient(rawClient || {});
    setClient(normalized);
    setClientForm(createClientForm(normalized));
    setSelectedLeadStageId(normalized.leadStageId || '');
    setConfirmedLeadStageId(normalized.leadStageId || '');
    setConfirmedLeadStatusId(normalized.leadStatusId || '');
    setConfirmedAssignedToUserId(normalized.assignedToUserId || '');
  };

  const applyMasterData = (masterData) => {
    setLeadStatuses(masterData?.leadStatuses || []);
    setLocations(masterData?.locations || []);
    setScopes(masterData?.scopes || []);
    setLeadSources(masterData?.leadSources || []);
    setLeadStages(masterData?.leadStages || []);
    setHandoverTimes(masterData?.handoverTimes || []);
  };

  const refreshClientDetails = async () => {
    const [clientRes, activitiesRes] = await Promise.all([
      api.get(`/clients/${id}`),
      api.get(`/clients/${id}/activities`),
    ]);

    applyClient(clientRes.data?.data);
    setActivities(activitiesRes.data?.data || []);
  };

  useEffect(() => {
    let isCurrent = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [clientRes, activitiesRes, masterDataRes, assignableUsersRes] = await getClientDetailInitialLoad(api, id);
        if (!isCurrent) {
          return;
        }

        applyClient(clientRes.data?.data);
        setActivities(activitiesRes.data?.data || []);
        applyMasterData(masterDataRes.data?.data);
        setAssignableUsers(assignableUsersRes.data?.data || []);
      } catch (err) {
        if (isCurrent) {
          setError(getApiErrorMessage(err, 'Could not load client details.'));
        }
      } finally {
        if (isCurrent) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id]);

  const handleClientFormChange = (key, value) => {
    setClientForm((prev) => {
      if (!prev) {
        return prev;
      }

      if (key === 'scopeId') {
        return {
          ...prev,
          scopeId: value,
        };
      }

      return { ...prev, [key]: value };
    });
  };

  const buildClientUpdatePayload = (override = {}) => {
    return {
      clientName: (override.clientName ?? clientForm?.name ?? '').trim(),
      email: (override.email ?? clientForm?.email) || null,
      phoneNumber: (override.phoneNumber ?? clientForm?.phoneNumber) || null,
      alternatePhoneNumber: (override.alternatePhoneNumber ?? clientForm?.alternatePhoneNumber) || null,
      locationId: toOptionalId(override.locationId ?? clientForm?.locationId),
      subLocationId: null,
      subLocationName: (override.subLocationName ?? clientForm?.subLocation) || null,
      scopeId: toOptionalId(override.scopeId ?? clientForm?.scopeId),
      subScopeId: null,
      subScopeName: (override.subScopeName ?? clientForm?.subScope) || null,
      budget: (override.budget ?? clientForm?.budget) || null,
      handoverTimeId: toOptionalId(override.handoverTimeId ?? clientForm?.handoverTimeId),
      leadSourceId: toOptionalId(override.leadSourceId ?? clientForm?.leadSourceId),
      leadStatusId: toOptionalId(override.leadStatusId ?? clientForm?.leadStatusId),
      leadStageId: toOptionalId(override.leadStageId ?? selectedLeadStageId),
      assignedToUserId: toOptionalId(override.assignedToUserId ?? clientForm?.assignedToUserId),
      holdUntilDate: (override.holdUntilDate ?? clientForm?.holdUntilDate) || null,
      createdAt: toApiDateTimeFromDateInput(override.createdAt ?? clientForm?.date),
      statusChangeComment: (override.statusChangeComment ?? null),
      notes: (override.notes ?? clientForm?.notes) || null,
    };
  };

  const handleUpdateClient = async () => {
    if (!clientForm?.name?.trim()) {
      setError('Client Name is required.');
      return;
    }

    if (!client?.clientId) {
      setError('Client is not loaded correctly.');
      return;
    }

    setUpdatingClient(true);
    setError('');
    setInfo('');

    try {
      const payload = buildClientUpdatePayload();

      const response = await api.put(`/clients/${client.clientId}`, payload);
      const normalized = normalizeClient(response.data?.data || {});

      setClient(normalized);
      setClientForm(createClientForm(normalized));
      setSelectedLeadStageId(normalized.leadStageId || selectedLeadStageId);
      setConfirmedLeadStageId(normalized.leadStageId || confirmedLeadStageId);
      setConfirmedLeadStatusId(normalized.leadStatusId || confirmedLeadStatusId);
      setConfirmedAssignedToUserId(normalized.assignedToUserId || confirmedAssignedToUserId);
      setInfo('Client information updated successfully.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not update client information.'));
    } finally {
      setUpdatingClient(false);
    }
  };

  const mapActivityTitle = (activity) => {
    const activityType = activity?.activityType || 'Activity';
    const dateValue = activity?.activityDate || activity?.createdAt;
    const when = formatDateTime(dateValue);

    if (activityType.toLowerCase() === 'lead created') {
      return `Lead created on ${when}`;
    }
    if (activityType.toLowerCase() === 'briefing call done') {
      return `Briefing call done on ${when}`;
    }
    if (activityType.toLowerCase() === 'comment added') {
      return `Comment added on ${when}`;
    }
    if (activityType.toLowerCase() === 'lead stage updated') {
      return `Lead stage updated on ${when}`;
    }
    return `${activityType} on ${when}`;
  };

  const mapActivityBy = (activity) => {
    const firstName = activity?.createdByUser?.firstName || '';
    const lastName = activity?.createdByUser?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || activity?.createdByUser?.email || 'Unknown user';
  };

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      const aTime = parseApiDateTime(a?.activityDate || a?.createdAt)?.getTime() ?? 0;
      const bTime = parseApiDateTime(b?.activityDate || b?.createdAt)?.getTime() ?? 0;
      return aTime - bTime;
    });
  }, [activities]);

  const isBriefingCallActivity = (activity) => String(activity?.activityType || '').trim().toLowerCase() === 'briefing call done';

  const latestComment = useMemo(() => {
    const withComment = [...activities]
      .sort((a, b) => {
        const aTime = parseApiDateTime(a?.activityDate || a?.createdAt)?.getTime() ?? 0;
        const bTime = parseApiDateTime(b?.activityDate || b?.createdAt)?.getTime() ?? 0;
        return bTime - aTime;
      })
      .find((activity) => String(activity?.notes || '').trim());
    if (!withComment) {
      return null;
    }

    return {
      text: withComment.notes,
      by: mapActivityBy(withComment),
      when: formatDateTime(withComment.activityDate || withComment.createdAt),
    };
  }, [activities]);

  const openConfirmPopup = () => {
    if (!commentText.trim() && !briefingCallHappened) {
      setError('Enter a comment or enable briefing call happened.');
      return;
    }
    setError('');
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setSaving(true);
    setError('');
    setInfo('');

    try {
      const payload = {
        comment: commentText.trim() || null,
        briefingCallHappened,
      };

      const response = await api.post(`/clients/${id}/activities`, payload);
      setActivities(response.data?.data || []);
      setInfo('Activity saved successfully.');
      setCommentText('');
      setBriefingCallHappened(false);
      setConfirmOpen(false);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not save activity.'));
    } finally {
      setSaving(false);
    }
  };

  const stageNameById = (value) => {
    const numericId = Number(value);
    const stage = leadStages.find((item) => Number(item?.leadStageId) === numericId);
    return stage?.stageName || 'None';
  };

  const statusNameById = (value) => {
    const numericId = Number(value);
    const status = leadStatuses.find((item) => Number(item?.leadStatusId) === numericId);
    return status?.status || 'None';
  };

  const handleLeadStatusSelection = (event) => {
    const nextStatusId = event.target.value;
    handleClientFormChange('leadStatusId', nextStatusId);

    if (!nextStatusId || nextStatusId === confirmedLeadStatusId) {
      return;
    }

    setPendingLeadStatusId(nextStatusId);
    setStatusComment('');
    setStatusReactivateDate('');
    setStatusConfirmOpen(true);
  };

  const closeStatusConfirm = () => {
    setStatusConfirmOpen(false);
    setPendingLeadStatusId('');
    setStatusComment('');
    setStatusReactivateDate('');
    handleClientFormChange('leadStatusId', confirmedLeadStatusId);
  };

  const pendingStatusName = (() => {
    const numericId = Number(pendingLeadStatusId);
    const status = leadStatuses.find((item) => Number(item?.leadStatusId) === numericId);
    return String(status?.status || '').trim().toLowerCase();
  })();
  const pendingIsHoldStatus = pendingStatusName === 'hold';
  const pendingIsInactiveStatus = pendingStatusName === 'inactive';

  const handleConfirmStatusChange = async () => {
    if (!pendingLeadStatusId || !client?.clientId) {
      closeStatusConfirm();
      return;
    }

    if ((pendingIsHoldStatus || pendingIsInactiveStatus) && !statusComment.trim()) {
      setError('Comment is required when moving lead status to Hold or InActive.');
      return;
    }

    if (pendingIsHoldStatus && !statusReactivateDate) {
      setError('When to active it date is required for Hold status.');
      return;
    }

    setStatusSaving(true);
    setError('');
    setInfo('');

    try {
      const payload = buildClientUpdatePayload({
        leadStatusId: pendingLeadStatusId,
        holdUntilDate: pendingIsHoldStatus ? statusReactivateDate : null,
        statusChangeComment: (pendingIsHoldStatus || pendingIsInactiveStatus) ? statusComment.trim() : null,
      });
      const response = await api.put(`/clients/${client.clientId}`, payload);
      const normalized = normalizeClient(response.data?.data || {});

      setClient(normalized);
      setClientForm(createClientForm(normalized));
      setConfirmedLeadStatusId(normalized.leadStatusId || pendingLeadStatusId);
      try {
        await refreshClientDetails();
      } catch (refreshErr) {
        console.error('Failed to refresh client details after lead status update', refreshErr);
      }
      setInfo('Lead status updated successfully.');
      setStatusConfirmOpen(false);
      setPendingLeadStatusId('');
      setStatusComment('');
      setStatusReactivateDate('');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not update lead status.'));
      handleClientFormChange('leadStatusId', confirmedLeadStatusId);
    } finally {
      setStatusSaving(false);
    }
  };

  const handleLeadStageSelection = (event) => {
    const nextStageId = event.target.value;
    setSelectedLeadStageId(nextStageId);

    if (!nextStageId || nextStageId === confirmedLeadStageId) {
      return;
    }

    setPendingLeadStageId(nextStageId);
    setStageConfirmOpen(true);
  };

  const assignedUserNameById = (value) => {
    if (!value) {
      return 'Unassigned';
    }
    const target = assignableUsers.find((item) => String(item?.userId) === String(value));
    if (target) {
      return `${target.firstName || ''} ${target.lastName || ''}`.trim() || target.email || 'Selected user';
    }
    if (String(client?.assignedToUserId || '') === String(value)) {
      return client?.assignedToName || `User ${value}`;
    }
    return `User ${value}`;
  };

  const handleAssignedToSelection = (event) => {
    const nextAssignedToUserId = event.target.value;
    handleClientFormChange('assignedToUserId', nextAssignedToUserId);

    if (String(nextAssignedToUserId || '') === String(confirmedAssignedToUserId || '')) {
      return;
    }

    setPendingAssignedToUserId(nextAssignedToUserId);
    setAssignmentConfirmOpen(true);
  };

  const closeAssignmentConfirm = () => {
    setAssignmentConfirmOpen(false);
    setPendingAssignedToUserId('');
    handleClientFormChange('assignedToUserId', confirmedAssignedToUserId || '');
  };

  const handleConfirmAssignmentChange = async () => {
    if (!client?.clientId) {
      closeAssignmentConfirm();
      return;
    }

    setAssignmentSaving(true);
    setError('');
    setInfo('');

    try {
      const payload = buildClientUpdatePayload({
        assignedToUserId: pendingAssignedToUserId || null,
      });

      const response = await api.put(`/clients/${client.clientId}`, payload);
      const normalized = normalizeClient(response.data?.data || {});

      setClient(normalized);
      setClientForm(createClientForm(normalized));
      setConfirmedAssignedToUserId(normalized.assignedToUserId || pendingAssignedToUserId || '');
      setInfo('Client assignment updated successfully.');
      setAssignmentConfirmOpen(false);
      setPendingAssignedToUserId('');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not update assignment.'));
      handleClientFormChange('assignedToUserId', confirmedAssignedToUserId || '');
    } finally {
      setAssignmentSaving(false);
    }
  };

  const assignmentOptions = useMemo(() => {
    const base = [...assignableUsers];
    const currentId = client?.assignedToUserId;

    if (currentId && !base.some((item) => String(item?.userId) === String(currentId))) {
      base.unshift({
        userId: Number(currentId),
        firstName: client?.assignedToName || 'Current',
        lastName: '',
        email: '',
        roleName: 'Current',
      });
    }

    return base;
  }, [assignableUsers, client?.assignedToName, client?.assignedToUserId]);

  const closeStageConfirm = () => {
    setStageConfirmOpen(false);
    setPendingLeadStageId('');
    setSelectedLeadStageId(confirmedLeadStageId);
  };

  const handleConfirmStageChange = async () => {
    if (!pendingLeadStageId) {
      closeStageConfirm();
      return;
    }

    setStageSaving(true);
    setError('');
    setInfo('');

    try {
      const response = await api.post(`/clients/${id}/lead-stage`, {
        leadStageId: Number(pendingLeadStageId),
      });

      const updatedClientRaw = response.data?.data?.client;
      const updatedActivities = response.data?.data?.activities || [];

      if (updatedClientRaw) {
        const normalized = normalizeClient(updatedClientRaw);
        setClient(normalized);
        setClientForm(createClientForm(normalized));
        setSelectedLeadStageId(normalized.leadStageId || pendingLeadStageId);
        setConfirmedLeadStageId(normalized.leadStageId || pendingLeadStageId);
      } else {
        setSelectedLeadStageId(pendingLeadStageId);
        setConfirmedLeadStageId(pendingLeadStageId);
      }

      setActivities(updatedActivities);
      setInfo('Lead stage updated successfully.');
      setStageConfirmOpen(false);
      setPendingLeadStageId('');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not update lead stage.'));
      setSelectedLeadStageId(confirmedLeadStageId);
    } finally {
      setStageSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack sx={{ minHeight: '60vh' }} alignItems="center" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 3,
          mb: 3,
          color: '#f8f9ff',
          background: 'radial-gradient(circle at 10% 20%, #1f3a5f 0%, #0a101a 52%, #091423 100%)',
        }}
      >
        <Container maxWidth="xl">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                Client Details
              </Typography>
              <Typography sx={{ opacity: 0.84 }}>Client: {client?.name || '-'}</Typography>
            </Box>
            <Button color="inherit" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ flex: 1, pb: 3 }}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {info && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {info}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Assigned To
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Assigned To</InputLabel>
                <Select
                  value={clientForm?.assignedToUserId || ''}
                  label="Assigned To"
                  onChange={handleAssignedToSelection}
                  disabled={assignmentSaving || updatingClient || stageSaving || statusSaving || saving}
                >
                  {isAdminUser && <MenuItem value="">Unassigned</MenuItem>}
                  {assignmentOptions.map((item) => (
                    <MenuItem key={item.userId} value={String(item.userId)}>
                      {`${item.firstName || ''} ${item.lastName || ''}`.trim()} ({item.roleName || '-'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Lead Status
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Lead Status</InputLabel>
                    <Select
                      value={clientForm?.leadStatusId || ''}
                      label="Lead Status"
                      onChange={handleLeadStatusSelection}
                      disabled={statusSaving}
                    >
                      {leadStatuses.map((item) => (
                        <MenuItem key={item.leadStatusId} value={String(item.leadStatusId)}>
                          {item.status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Lead Stage
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel id="lead-stage-select-label">Lead Stage</InputLabel>
                    <Select
                      labelId="lead-stage-select-label"
                      value={selectedLeadStageId}
                      label="Lead Stage"
                      onChange={handleLeadStageSelection}
                      disabled={stageSaving}
                    >
                      <MenuItem value="">None</MenuItem>
                      {leadStages.map((stage) => (
                        <MenuItem key={stage.leadStageId} value={String(stage.leadStageId)}>
                          {stage.stageName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Client Information
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Client Name"
                    value={clientForm?.name || ''}
                    onChange={(event) => handleClientFormChange('name', event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={clientForm?.phoneNumber || ''}
                    onChange={(event) => handleClientFormChange('phoneNumber', event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={clientForm?.email || ''}
                    onChange={(event) => handleClientFormChange('email', event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={clientForm?.locationId || ''}
                      label="Location"
                      onChange={(event) => handleClientFormChange('locationId', event.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {locations.map((item) => (
                        <MenuItem key={item.locationId} value={String(item.locationId)}>
                          {item.locationName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Sub Location"
                    value={clientForm?.subLocation || ''}
                    onChange={(event) => handleClientFormChange('subLocation', event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Lead Source</InputLabel>
                    <Select
                      value={clientForm?.leadSourceId || ''}
                      label="Lead Source"
                      onChange={(event) => handleClientFormChange('leadSourceId', event.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {leadSources.map((item) => (
                        <MenuItem key={item.leadSourceId} value={String(item.leadSourceId)}>
                          {item.sourceName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date"
                    value={clientForm?.date || ''}
                    onChange={(event) => handleClientFormChange('date', event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Scope</InputLabel>
                    <Select
                      value={clientForm?.scopeId || ''}
                      label="Scope"
                      onChange={(event) => handleClientFormChange('scopeId', event.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {scopes.map((item) => (
                        <MenuItem key={item.scopeId} value={String(item.scopeId)}>
                          {item.scopeName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Sub Scope"
                    value={clientForm?.subScope || ''}
                    onChange={(event) => handleClientFormChange('subScope', event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Handover</InputLabel>
                    <Select
                      value={clientForm?.handoverTimeId || ''}
                      label="Handover"
                      onChange={(event) => handleClientFormChange('handoverTimeId', event.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {handoverTimes.map((item) => (
                        <MenuItem key={item.handoverTimeId} value={String(item.handoverTimeId)}>
                          {item.timeframe}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Budget"
                    value={clientForm?.budget || ''}
                    onChange={(event) => handleClientFormChange('budget', event.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Notes"
                    value={clientForm?.notes || ''}
                    onChange={(event) => handleClientFormChange('notes', event.target.value)}
                  />
                </Grid>
              </Grid>

              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleUpdateClient}
                  disabled={updatingClient || stageSaving || statusSaving || assignmentSaving || saving}
                >
                  {updatingClient ? 'Updating...' : 'Update Client'}
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Latest Activity
                  </Typography>
                  {latestComment ? (
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {latestComment.text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {latestComment.when} by {latestComment.by}
                      </Typography>
                    </Paper>
                  ) : (
                    <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                      No comments yet.
                    </Typography>
                  )}

                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Activity History
                  </Typography>

                  {sortedActivities.length === 0 ? (
                    <Typography color="text.secondary">No activity found.</Typography>
                  ) : (
                    <Box sx={{ maxHeight: { xs: 280, md: 420 }, overflowY: 'auto', pr: 1 }}>
                      <Stepper orientation="vertical" sx={{ pl: 0.5 }}>
                        {sortedActivities.map((activity) => (
                          <Step key={activity.activityId} active>
                            <StepLabel>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {isBriefingCallActivity(activity) && <CallIcon fontSize="small" sx={{ color: 'success.main' }} />}
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  {mapActivityTitle(activity)}
                                </Typography>
                              </Stack>
                            </StepLabel>
                            <StepContent>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {activity.notes || '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Added by {mapActivityBy(activity)}
                              </Typography>
                            </StepContent>
                          </Step>
                        ))}
                      </Stepper>
                    </Box>
                  )}
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Add Comment
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={6}
                    placeholder="Enter comment"
                    value={commentText}
                    onChange={(event) => setCommentText(event.target.value)}
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => navigate('/dashboard')}>
              Cancel
            </Button>
            <Button onClick={openConfirmPopup} variant="contained" startIcon={<SaveIcon />} disabled={saving || stageSaving}>
              Save Comment
            </Button>
          </Stack>
        </Paper>
      </Container>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Are you sure to save comment?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will add a new activity record in history.
          </Typography>
          <FormControlLabel
            control={<Switch checked={briefingCallHappened} onChange={(event) => setBriefingCallHappened(event.target.checked)} />}
            label="Briefing call happened"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirmSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={stageConfirmOpen} onClose={closeStageConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Lead Stage Change</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you are moving from {stageNameById(confirmedLeadStageId)} to {stageNameById(pendingLeadStageId)}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStageConfirm} disabled={stageSaving}>
            Cancel
          </Button>
          <Button onClick={handleConfirmStageChange} variant="contained" disabled={stageSaving}>
            {stageSaving ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusConfirmOpen} onClose={closeStatusConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Lead Status Change</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: (pendingIsHoldStatus || pendingIsInactiveStatus) ? 2 : 0 }}>
            Are you sure you want to move from {statusNameById(confirmedLeadStatusId)} to {statusNameById(pendingLeadStatusId)}?
          </Typography>
          {(pendingIsHoldStatus || pendingIsInactiveStatus) && (
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Comment"
              value={statusComment}
              onChange={(event) => setStatusComment(event.target.value)}
              placeholder="Enter comment"
              required
              sx={{ mb: pendingIsHoldStatus ? 2 : 0 }}
            />
          )}
          {pendingIsHoldStatus && (
            <TextField
              fullWidth
              type="date"
              label="When to active it"
              value={statusReactivateDate}
              onChange={(event) => setStatusReactivateDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusConfirm} disabled={statusSaving}>
            Cancel
          </Button>
          <Button onClick={handleConfirmStatusChange} variant="contained" disabled={statusSaving}>
            {statusSaving ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignmentConfirmOpen} onClose={closeAssignmentConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Assignment Change</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to assign this client from {assignedUserNameById(confirmedAssignedToUserId)} to{' '}
            {assignedUserNameById(pendingAssignedToUserId)}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignmentConfirm} disabled={assignmentSaving}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAssignmentChange} variant="contained" disabled={assignmentSaving}>
            {assignmentSaving ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDetailPage;
